import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface LoyaltyTier {
  name: string;
  minPoints: number;
  maxPoints: number;
  color: string;
  benefits: string[];
}

const LOYALTY_TIERS: LoyaltyTier[] = [
  { name: 'Bronze', minPoints: 0, maxPoints: 499, color: 'text-amber-600', benefits: ['1x points on purchases'] },
  { name: 'Silver', minPoints: 500, maxPoints: 1499, color: 'text-slate-400', benefits: ['1.25x points', '5% exclusive discounts'] },
  { name: 'Gold', minPoints: 1500, maxPoints: 3999, color: 'text-yellow-500', benefits: ['1.5x points', '10% exclusive discounts', 'Early access'] },
  { name: 'Platinum', minPoints: 4000, maxPoints: Infinity, color: 'text-cyan-400', benefits: ['2x points', '15% exclusive discounts', 'VIP access', 'Free shipping'] },
];

interface LoyaltyContextType {
  points: number;
  lifetimePoints: number;
  currentTier: LoyaltyTier;
  nextTier: LoyaltyTier | null;
  pointsToNextTier: number;
  redeemPoints: (amount: number, rewardName: string) => Promise<boolean>;
  refreshLoyalty: () => Promise<void>;
}

const LoyaltyContext = createContext<LoyaltyContextType | undefined>(undefined);

export const LoyaltyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [points, setPoints] = useState(0);
  const [lifetimePoints, setLifetimePoints] = useState(0);
  const [previousTier, setPreviousTier] = useState<LoyaltyTier | null>(null);

  const getCurrentTier = (pts: number): LoyaltyTier => {
    return LOYALTY_TIERS.find(tier => pts >= tier.minPoints && pts <= tier.maxPoints) || LOYALTY_TIERS[0];
  };

  const getNextTier = (currentTier: LoyaltyTier): LoyaltyTier | null => {
    const currentIndex = LOYALTY_TIERS.findIndex(t => t.name === currentTier.name);
    return currentIndex < LOYALTY_TIERS.length - 1 ? LOYALTY_TIERS[currentIndex + 1] : null;
  };

  const currentTier = getCurrentTier(lifetimePoints);
  const nextTier = getNextTier(currentTier);
  const pointsToNextTier = nextTier ? nextTier.minPoints - lifetimePoints : 0;

  const refreshLoyalty = useCallback(async () => {
    if (!user) {
      setPoints(0);
      setLifetimePoints(0);
      return;
    }

    const { data, error } = await supabase
      .from('loyalty_points')
      .select('points, lifetime_points')
      .eq('user_id', user.id)
      .single();

    if (!error && data) {
      setPoints(data.points || 0);
      setLifetimePoints(data.lifetime_points || 0);
    }
  }, [user]);

  useEffect(() => {
    refreshLoyalty();
  }, [refreshLoyalty]);

  // Check for tier changes and show notifications
  useEffect(() => {
    if (!user || lifetimePoints === 0) return;

    const newTier = getCurrentTier(lifetimePoints);
    
    if (previousTier && newTier.name !== previousTier.name) {
      const tierIndex = LOYALTY_TIERS.findIndex(t => t.name === newTier.name);
      const prevIndex = LOYALTY_TIERS.findIndex(t => t.name === previousTier.name);
      
      if (tierIndex > prevIndex) {
        toast.success(`🎉 Congratulations! You've reached ${newTier.name} tier!`, {
          description: `Enjoy benefits: ${newTier.benefits.join(', ')}`,
          duration: 6000,
        });
      }
    }
    
    setPreviousTier(newTier);
  }, [lifetimePoints, user, previousTier]);

  // Check if close to next tier
  useEffect(() => {
    if (!user || !nextTier) return;

    const percentToNext = ((lifetimePoints - currentTier.minPoints) / (nextTier.minPoints - currentTier.minPoints)) * 100;
    
    if (percentToNext >= 80 && percentToNext < 100) {
      const shown = sessionStorage.getItem(`tier-notify-${nextTier.name}`);
      if (!shown) {
        toast.info(`🔥 Almost there! Only ${pointsToNextTier} points to ${nextTier.name} tier!`, {
          duration: 5000,
        });
        sessionStorage.setItem(`tier-notify-${nextTier.name}`, 'true');
      }
    }
  }, [lifetimePoints, currentTier, nextTier, pointsToNextTier, user]);

  const redeemPoints = async (amount: number, rewardName: string): Promise<boolean> => {
    if (!user) {
      toast.error('Please log in to redeem rewards');
      return false;
    }

    if (points < amount) {
      toast.error('Not enough points');
      return false;
    }

    const { data, error } = await supabase.rpc('redeem_loyalty_points', {
      p_points: amount,
      p_description: `Redeemed: ${rewardName}`,
    });

    if (error) {
      toast.error(error.message || 'Failed to redeem reward');
      return false;
    }

    toast.success(`Successfully redeemed ${rewardName}!`);
    await refreshLoyalty();
    return true;
  };

  return (
    <LoyaltyContext.Provider
      value={{
        points,
        lifetimePoints,
        currentTier,
        nextTier,
        pointsToNextTier,
        redeemPoints,
        refreshLoyalty,
      }}
    >
      {children}
    </LoyaltyContext.Provider>
  );
};

export const useLoyalty = () => {
  const context = useContext(LoyaltyContext);
  if (!context) throw new Error('useLoyalty must be used within LoyaltyProvider');
  return context;
};
